/**
 * Expandable row list for paper-journal durable rows (Offers-list pattern).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { PaperJournalRewardRow } from '@/app/types/paperJournal';
import { Badge } from '@/components/common/Badge';
import { DataList } from '@/components/common/DataList';
import { paperJournalRowToDataListItems } from '@/pages/PaperJournal/paperJournalRowDataList';
import {
  paperJournalEpidLink,
  paperJournalPartnerLink,
  paperJournalRefUserLink,
} from '@/pages/PaperJournal/paperJournalAdminLinks';
import { formatPaperJournalPlainInt } from '@/pages/PaperJournal/paperJournalFormat';
import { paperJournalStatusBadgeVariant } from '@/pages/PaperJournal/paperJournalStatusBadgeVariant';

import './PaperJournalExpandableRowList.scss';

const WIDE_VIEWPORT_QUERY = '(min-width: 769px)';

function useWideViewport(): boolean {
  const [wide, setWide] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia(WIDE_VIEWPORT_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }
    const mq = window.matchMedia(WIDE_VIEWPORT_QUERY);
    const onChange = (): void => setWide(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return wide;
}

export interface PaperJournalExpandableRowListProps {
  rows: PaperJournalRewardRow[];
  loading?: boolean;
  emptyMessage?: string;
}

function _failurePreview(reason: string | null): string | null {
  if (reason === null || reason === '') {
    return null;
  }
  if (reason.length <= 80) {
    return reason;
  }
  return `${reason.slice(0, 77)}…`;
}

export const PaperJournalExpandableRowList: React.FC<PaperJournalExpandableRowListProps> = ({
  rows,
  loading = false,
  emptyMessage = 'No rows to display.',
}) => {
  const wideViewport = useWideViewport();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const rowIds = useMemo(() => rows.map((r) => r.id), [rows]);

  const toggleExpand = useCallback((rowId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedIds(new Set(rowIds));
  }, [rowIds]);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  if (loading) {
    return (
      <div className="paper-journal-expandable-rows paper-journal-expandable-rows--loading">
        <div className="paper-journal-expandable-rows__spinner" aria-hidden />
        <span>Loading rows…</span>
      </div>
    );
  }

  if (rows.length === 0) {
    return <div className="paper-journal-expandable-rows__empty">{emptyMessage}</div>;
  }

  return (
    <div className="paper-journal-expandable-rows">
      <div className="paper-journal-expandable-rows__toolbar">
        <span className="paper-journal-expandable-rows__summary">
          {rows.length} row{rows.length !== 1 ? 's' : ''}
        </span>
        <div className="paper-journal-expandable-rows__expand-controls">
          <button type="button" className="btn-text" onClick={expandAll}>
            Expand all
          </button>
          <button type="button" className="btn-text" onClick={collapseAll}>
            Collapse all
          </button>
        </div>
      </div>

      <div className="paper-journal-expandable-rows__list">
        {rows.map((row) => {
          const isExpanded = expandedIds.has(row.id);
          const failurePreview = _failurePreview(row.failure_reason);

          return (
            <article
              key={row.id}
              className={`paper-journal-expandable-rows__group${isExpanded ? ' is-expanded' : ''}`}
            >
              <button
                type="button"
                className="paper-journal-expandable-rows__header"
                aria-expanded={isExpanded}
                onClick={() => toggleExpand(row.id)}
                data-testid={`pj-row-header-${row.id}`}
              >
                <span
                  className={`paper-journal-expandable-rows__expand-icon${isExpanded ? ' is-expanded' : ''}`}
                  aria-hidden
                >
                  ▶
                </span>
                <span className="paper-journal-expandable-rows__csv-row">
                  CSV row {formatPaperJournalPlainInt(row.csv_row_index)}
                </span>
                <span className="paper-journal-expandable-rows__status">
                  <Badge variant={paperJournalStatusBadgeVariant(row.status)} size="sm">
                    {row.status}
                  </Badge>
                </span>
                <span className="paper-journal-expandable-rows__meta">
                  <span className="paper-journal-expandable-rows__meta-item">
                    <span className="paper-journal-expandable-rows__meta-label">Ref user</span>
                    <span className="paper-journal-expandable-rows__meta-value">
                      {paperJournalRefUserLink(row.ref_user_id, { stopPropagation: true })}
                    </span>
                  </span>
                  <span className="paper-journal-expandable-rows__meta-item">
                    <span className="paper-journal-expandable-rows__meta-label">EPID</span>
                    <span className="paper-journal-expandable-rows__meta-value">
                      {paperJournalEpidLink(row.eligible_participant_id, { stopPropagation: true })}
                    </span>
                  </span>
                  <span className="paper-journal-expandable-rows__meta-item">
                    <span className="paper-journal-expandable-rows__meta-label">Points</span>
                    <span className="paper-journal-expandable-rows__meta-value">
                      {formatPaperJournalPlainInt(row.reward_amount)}
                    </span>
                  </span>
                  <span className="paper-journal-expandable-rows__meta-item">
                    <span className="paper-journal-expandable-rows__meta-label">Partner</span>
                    <span className="paper-journal-expandable-rows__meta-value">
                      {paperJournalPartnerLink(row.partner_product_id, { stopPropagation: true })}
                    </span>
                  </span>
                </span>
                {failurePreview !== null && (
                  <span className="paper-journal-expandable-rows__failure-preview" title={row.failure_reason ?? undefined}>
                    {failurePreview}
                  </span>
                )}
              </button>

              {isExpanded && (
                <div className="paper-journal-expandable-rows__detail">
                  <DataList
                    className="paper-journal-expandable-rows__data-list"
                    items={paperJournalRowToDataListItems(row)}
                    columns={wideViewport ? 2 : 1}
                  />
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default PaperJournalExpandableRowList;
